import { useState, useEffect } from "react";
import { Link, useLocation } from "wouter";
import {
  Building, Users, CreditCard, MessageSquare, BarChart,
  Settings, Building2, Database, FileText, Wrench, MessageCircle,
  Menu, Shield, Calendar, TrendingDown, Scale, ClipboardList,
  BookOpen, Bell, LayoutDashboard, Sparkles, LogOut
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useLanguage } from "@/hooks/use-language";
import { useAuth } from "@/hooks/use-auth";

// ─── Clean & Organized Bilingual Navigation Sections ──────────────────────────
interface NavItem {
  nameEn: string;
  nameAr: string;
  href: string;
  icon: any;
  key: string;
  badge?: string;
  badgeColor?: string;
}

interface NavSection {
  titleEn: string;
  titleAr: string;
  items: NavItem[];
}

const navigationSections: NavSection[] = [
  {
    titleEn: "Overview & Properties",
    titleAr: "الرئيسية والعقارات",
    items: [
      { nameEn: "Dashboard", nameAr: "لوحة التحكم", href: "/", icon: LayoutDashboard, key: "dashboard" },
      { nameEn: "Tenants", nameAr: "المستأجرين", href: "/tenants", icon: Users, key: "tenants" },
      { nameEn: "Units & Rooms", nameAr: "الوحدات والغرف", href: "/properties", icon: Building2, key: "properties" },
      { nameEn: "Reservations", nameAr: "طلبات الحجز", href: "/admin/reservations", icon: Calendar, key: "reservations" },
    ],
  },
  {
    titleEn: "Financials & Payments",
    titleAr: "المالية والتحصيل",
    items: [
      { nameEn: "Payments & Rent", nameAr: "المدفوعات والإيجارات", href: "/payment", icon: CreditCard, key: "payments" },
      { nameEn: "Invoices & Expenses", nameAr: "الفواتير والمصروفات", href: "/invoices", icon: FileText, key: "invoices" },
      { nameEn: "Debts & Cash", nameAr: "إدارة الديون ومطابقة الكاش", href: "/debt-management", icon: TrendingDown, key: "debt-management" },
    ],
  },
  {
    titleEn: "Messaging & AI",
    titleAr: "المراسلات والذكاء الاصطناعي",
    items: [
      { nameEn: "WhatsApp AI Agent", nameAr: "وكيل WhatsApp الذكي", href: "/whatsapp-ai", icon: Sparkles, key: "whatsapp-ai", badge: "AI", badgeColor: "bg-emerald-100 text-emerald-800" },
      { nameEn: "WhatsApp & Meta Suite", nameAr: "محادثات ميتا وواتساب", href: "/meta-inbox", icon: MessageSquare, key: "meta-inbox", badge: "Meta", badgeColor: "bg-mint-soft text-ink" },
      { nameEn: "WhatsApp & SMS", nameAr: "مراسلات الواتساب وSMS", href: "/whatsapp", icon: MessageCircle, key: "whatsapp" },
      { nameEn: "Scheduled Alerts", nameAr: "التنبيهات المجدولة", href: "/scheduled-alerts", icon: Bell, key: "scheduled-alerts" },
    ],
  },
  {
    titleEn: "Reports & System",
    titleAr: "التقارير وإعدادات النظام",
    items: [
      { nameEn: "Reports & Analytics", nameAr: "التقارير والتحليلات", href: "/reports", icon: BarChart, key: "reports" },
      { nameEn: "Company Settings", nameAr: "إعدادات المنصة والشركة", href: "/company-settings", icon: Settings, key: "company-settings" },
      { nameEn: "Team & Permissions", nameAr: "فريق العمل والصلاحيات", href: "/team", icon: Shield, key: "team" },
      { nameEn: "Audit & Backup", nameAr: "سجل العمليات والنسخ", href: "/audit-log", icon: ClipboardList, key: "audit-log" },
    ],
  },
];

interface SidebarContentProps {
  onLinkClick?: () => void;
}

export function SidebarContent({ onLinkClick }: SidebarContentProps) {
  const [location] = useLocation();
  const { language, dir } = useLanguage();
  const { user, logout } = useAuth();
  const isAr = language === "ar";

  const companyName = user?.company?.name || "Rently";
  const companyTagline = user?.company?.headerTagline || (isAr ? "نظام إدارة العقارات والإيجارات" : "Property & Rent Management");
  const logoUrl = user?.company?.logoUrl;

  const initials = (user?.fullName || "").trim().split(/\s+/).slice(0, 2).map((p) => p[0]?.toUpperCase()).join("") || "U";

  return (
    <div 
      dir={dir} 
      className={cn(
        "flex flex-col h-full bg-white text-slate-800 shadow-xs",
        isAr ? "border-l border-slate-200/80" : "border-r border-slate-200/80"
      )}
    >
      {/* Brand Header */}
      <div className="flex items-center justify-between px-5 py-5 border-b border-mint/30 bg-mint-soft/80">
        <Link href="/" className={cn("flex items-center gap-3 group w-full", isAr ? "text-right" : "text-left")} onClick={onLinkClick}>
          {logoUrl ? (
            <div className="h-10 w-10 rounded-2xl overflow-hidden bg-white border border-slate-200 flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform">
              <img src={logoUrl} alt={companyName} className="h-full w-full object-contain" />
            </div>
          ) : (
            <div className="h-10 w-10 rounded-2xl bg-primary text-primary-foreground ring-2 ring-mint flex items-center justify-center shadow-md group-hover:scale-105 transition-transform shrink-0">
              <Building className="h-5 w-5" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h1 className="text-base font-black text-slate-900 tracking-tight leading-tight truncate">
                {companyName}
              </h1>
              <span className="text-[10px] bg-accent/10 border border-accent/30 text-accent font-black px-2 py-0.5 rounded-md shrink-0">
                {isAr ? "لوحة الإدارة" : "Admin"}
              </span>
            </div>
            <p className="text-[10px] text-slate-400 font-bold truncate mt-0.5">
              {companyTagline}
            </p>
          </div>
        </Link>
      </div>

      {/* Navigation Scroll Area */}
      <div className="flex-1 overflow-y-auto px-3 py-4 space-y-5 custom-scrollbar">
        {navigationSections.map((section) => (
          <div key={section.titleEn} className="space-y-1">
            <p className={cn(
              "px-3 text-[11px] font-black text-slate-400 mb-1.5 flex items-center justify-between",
              isAr ? "text-right" : "text-left"
            )}>
              <span>{isAr ? section.titleAr : section.titleEn}</span>
            </p>
            {section.items.map((item) => {
              const Icon = item.icon;
              const isActive = location === item.href || (item.href !== "/" && location.startsWith(item.href));
              
              return (
                <Link
                  key={item.key}
                  href={item.href}
                  onClick={onLinkClick}
                  className={cn(
                    "group flex items-center justify-between px-3.5 py-2.5 text-xs rounded-xl transition-all duration-150 font-bold",
                    isAr ? "text-right" : "text-left",
                    isActive
                      ? "bg-primary text-primary-foreground shadow-md shadow-primary/20 font-black"
                      : "text-slate-700 hover:bg-mint-soft hover:text-ink"
                  )}
                  data-testid={`nav-link-${item.key}`}
                >
                  <div className="flex items-center gap-2.5 min-w-0">
                    <Icon className={cn(
                      "h-4 w-4 shrink-0 transition-colors",
                      isActive ? "text-primary-foreground" : "text-slate-500 group-hover:text-slate-700"
                    )} />
                    <span className="truncate">{isAr ? item.nameAr : item.nameEn}</span>
                  </div>
                  {item.badge && (
                    <span className={cn(
                      "text-[9px] px-1.5 py-0.2 rounded-full font-black tracking-wider shadow-2xs",
                      isActive ? "bg-white text-primary" : (item.badgeColor || "bg-slate-100 text-slate-700")
                    )}>
                      {item.badge}
                    </span>
                  )}
                </Link>
              );
            })}
          </div>
        ))}
      </div>

      {/* User Footer */}
      <div className="shrink-0 border-t border-slate-100 p-3.5 bg-slate-50/70">
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 rounded-xl bg-primary text-primary-foreground ring-2 ring-mint flex items-center justify-center text-xs font-black shadow-sm shrink-0">
            {initials}
          </div>
          <div className={cn("flex-1 min-w-0", isAr ? "text-right" : "text-left")}>
            <p className="text-xs font-black text-slate-800 truncate">{user?.fullName || (isAr ? "مستخدم" : "User")}</p>
            <p className="text-[10px] text-slate-500 font-semibold truncate">
              {user?.roleLabel || user?.role || ""}
            </p>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={logout}
            className="h-8 w-8 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg shrink-0"
            title={isAr ? "تسجيل الخروج" : "Sign out"}
            data-testid="button-logout"
          >
            <LogOut className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}

interface ResponsiveSidebarProps {
  mobileMenuOpen: boolean;
  setMobileMenuOpen: (open: boolean) => void;
}

export function ResponsiveSidebar({ mobileMenuOpen, setMobileMenuOpen }: ResponsiveSidebarProps) {
  const { language } = useLanguage();
  const isAr = language === "ar";

  return (
    <>
      {/* Desktop sidebar */}
      <div className="hidden md:flex md:flex-shrink-0">
        <div className="flex flex-col w-64">
          <SidebarContent />
        </div>
      </div>

      {/* Mobile sidebar */}
      <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
        <SheetContent side={isAr ? "right" : "left"} className="p-0 w-72">
          <SheetHeader className="sr-only">
            <SheetTitle>Navigation Menu</SheetTitle>
          </SheetHeader>
          <SidebarContent onLinkClick={() => setMobileMenuOpen(false)} />
        </SheetContent>
      </Sheet>
    </>
  );
}

export function MobileMenuButton({ onClick }: { onClick: () => void }) {
  const { language } = useLanguage();
  const isAr = language === "ar";

  return (
    <Button
      variant="ghost"
      size="sm"
      className={cn(
        "px-3 text-slate-600 md:hidden",
        isAr ? "border-l border-slate-200" : "border-r border-slate-200"
      )}
      data-testid="mobile-menu-button"
      onClick={onClick}
    >
      <Menu className="h-5 w-5" />
    </Button>
  );
}
